/**
 * StreakBadge Component
 * 
 * Displays the win streak number inside a decorative laurel badge.
 * Used on the GameOver celebration screen for solved puzzles.
 * 
 * Easter egg: Laurel pieces respond to touch/mouse with spring physics!
 */

import { useEffect, useRef, useCallback } from 'react';
import './StreakBadge.css';

interface StreakBadgeProps {
    streak: number;
    className?: string;
}

// Physics constants
const SPRING_STIFFNESS = 0.08;
const SPRING_DAMPING = 0.85;
const REPEL_RADIUS = 60;
const REPEL_FORCE = 15;

interface Particle {
    x: number;
    y: number;
    vx: number;
    vy: number;
    originX: number;
    originY: number;
    rotation: number;
    vr: number;
}

export function StreakBadge({ streak, className }: StreakBadgeProps) {
    const svgRef = useRef<SVGSVGElement>(null);
    const particlesRef = useRef<Particle[]>([]);
    const animationRef = useRef<number>(0);
    const mouseRef = useRef({ x: -1000, y: -1000 });
    const isInitializedRef = useRef(false);

    // Initialize particle physics for each path
    const initParticles = useCallback(() => {
        if (!svgRef.current || isInitializedRef.current) return;

        const paths = svgRef.current.querySelectorAll('.streak-part');
        particlesRef.current = Array.from(paths).map(() => ({
            x: 0,
            y: 0,
            vx: 0,
            vy: 0,
            originX: 0,
            originY: 0,
            rotation: 0,
            vr: 0,
        }));
        isInitializedRef.current = true;
    }, []);

    // Physics simulation step
    const simulate = useCallback(() => {
        if (!svgRef.current) return;

        const paths = svgRef.current.querySelectorAll('.streak-part');
        const rect = svgRef.current.getBoundingClientRect();
        const scaleX = 180 / rect.width;
        const scaleY = 174 / rect.height;

        // Convert mouse position to SVG coordinates
        const mx = (mouseRef.current.x - rect.left) * scaleX;
        const my = (mouseRef.current.y - rect.top) * scaleY;

        paths.forEach((path, i) => {
            const p = particlesRef.current[i];
            if (!p) return;

            // Get center of this path
            const bbox = (path as SVGPathElement).getBBox();
            const cx = bbox.x + bbox.width / 2;
            const cy = bbox.y + bbox.height / 2;

            // Distance from mouse to path center
            const dx = cx - mx;
            const dy = cy - my;
            const dist = Math.sqrt(dx * dx + dy * dy);

            // Repel force if mouse is close
            if (dist < REPEL_RADIUS && dist > 0) {
                const force = (1 - dist / REPEL_RADIUS) * REPEL_FORCE;
                const angle = Math.atan2(dy, dx);
                p.vx += Math.cos(angle) * force;
                p.vy += Math.sin(angle) * force;
                // Add rotation based on approach direction
                p.vr += (dx > 0 ? 1 : -1) * force * 0.5;
            }

            // Spring back to origin
            p.vx += (p.originX - p.x) * SPRING_STIFFNESS;
            p.vy += (p.originY - p.y) * SPRING_STIFFNESS;
            p.vr += (0 - p.rotation) * SPRING_STIFFNESS;

            // Apply damping
            p.vx *= SPRING_DAMPING;
            p.vy *= SPRING_DAMPING;
            p.vr *= SPRING_DAMPING;

            // Update position
            p.x += p.vx;
            p.y += p.vy;
            p.rotation += p.vr;

            // Apply transform
            (path as SVGPathElement).style.transform =
                `translate(${p.x}px, ${p.y}px) rotate(${p.rotation}deg)`;
        });

        animationRef.current = requestAnimationFrame(simulate);
    }, []);

    // Handle mouse/touch move
    const handlePointerMove = useCallback((e: MouseEvent | TouchEvent) => {
        if ('touches' in e && e.touches[0]) {
            mouseRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        } else if ('clientX' in e) {
            mouseRef.current = { x: e.clientX, y: e.clientY };
        }
    }, []);

    // Handle mouse/touch leave
    const handlePointerLeave = useCallback(() => {
        mouseRef.current = { x: -1000, y: -1000 };
    }, []);

    useEffect(() => {
        // Wait for entrance animation to complete before enabling physics
        const timer = setTimeout(() => {
            initParticles();
            animationRef.current = requestAnimationFrame(simulate);
        }, 800);

        // Add event listeners
        window.addEventListener('mousemove', handlePointerMove);
        window.addEventListener('touchmove', handlePointerMove);
        window.addEventListener('mouseleave', handlePointerLeave);
        window.addEventListener('touchend', handlePointerLeave);

        return () => {
            clearTimeout(timer);
            cancelAnimationFrame(animationRef.current);
            window.removeEventListener('mousemove', handlePointerMove);
            window.removeEventListener('touchmove', handlePointerMove);
            window.removeEventListener('mouseleave', handlePointerLeave);
            window.removeEventListener('touchend', handlePointerLeave);
        };
    }, [initParticles, simulate, handlePointerMove, handlePointerLeave]);

    return (
        <div className={`streak-badge ${className ?? ''}`}>
            <svg ref={svgRef} viewBox="0 0 180 174" fill="none" aria-hidden="true">
                <defs>
                    {/* Film grain texture overlay */}
                    <filter id="streak-noise">
                        <feTurbulence type="turbulence" baseFrequency="0.8" numOctaves="3" stitchTiles="stitch" result="noise" />
                        <feComponentTransfer in="noise" result="sharperNoise">
                            <feFuncR type="linear" slope="3" intercept="-1" />
                            <feFuncG type="linear" slope="3" intercept="-1" />
                            <feFuncB type="linear" slope="3" intercept="-1" />
                            <feFuncA type="linear" slope="0.5" />
                        </feComponentTransfer>
                        <feComposite operator="in" in="sharperNoise" in2="SourceGraphic" result="clippedNoise" />
                        <feBlend mode="overlay" in="clippedNoise" in2="SourceGraphic" />
                    </filter>
                </defs>

                <g filter="url(#streak-noise)" className="wreath-group">
                    <path className="streak-part" d="M47.724 8.90198V27.3075L31.2646 42.7456V25.8238L47.724 8.90198Z" fill="var(--color-accent-orange)" />
                    <path className="streak-part" d="M34.1689 61.1411H0.620117L15.7801 76.5793L34.1689 61.1411Z" fill="var(--color-accent-orange)" />
                    <path className="streak-part" d="M34.1689 78.2233L0.620117 76.5792L17.3945 93.6715L34.1689 78.2233Z" fill="var(--color-accent-orange)" />
                    <path className="streak-part" d="M14.4902 59.497L31.2646 42.7456L14.4902 43.728V59.497Z" fill="var(--color-accent-orange)" />
                    <path className="streak-part" d="M34.1689 44.0589V61.1411L67.7276 25.8239H50.9532L50.1952 43.2068L34.1689 44.0589Z" fill="var(--color-accent-orange)" />
                    <path className="streak-part" d="M132.936 8.90198V25.8238L149.444 42.7456L149.986 25.8238L132.936 8.90198Z" fill="var(--color-accent-orange)" />
                    <path className="streak-part" d="M147.396 76.5792H178.898L163.147 93.6715L147.396 76.5792Z" fill="var(--color-accent-orange)" />
                    <path className="streak-part" d="M61.7822 174L78.0841 156.637H61.7822V174Z" fill="var(--color-accent-orange)" />
                    <path className="streak-part" d="M91.0586 144.557L119.223 174L118.76 156.637L104.033 157.198L113.572 144.557H91.0586Z" fill="var(--color-accent-orange)" />
                    <path className="streak-part" d="M67.1562 145.219L90.0439 144.557L78.0832 156.637L67.1562 145.219Z" fill="var(--color-accent-orange)" />
                    <path className="streak-part" d="M113.572 144.557L129.884 127.956L130.534 144.938L113.572 144.557Z" fill="var(--color-accent-orange)" />
                    <path className="streak-part" d="M146.372 127.956L130.533 144.938H146.372V127.956Z" fill="var(--color-accent-orange)" />
                    <path className="streak-part" d="M129.884 127.956H147.396V110.593L129.884 127.956Z" fill="var(--color-accent-orange)" />
                    <path className="streak-part" d="M163.146 93.6715H180L163.146 111.255V128.427L146.372 127.956L162.211 111.044L146.372 111.606L163.146 93.6715Z" fill="var(--color-accent-orange)" />
                    <path className="streak-part" d="M16.833 111.185V128.327H33.1546L16.833 111.185Z" fill="var(--color-accent-orange)" />
                    <path className="streak-part" d="M33.5586 111.185L34.1689 128.327H50.3921L33.5586 111.185Z" fill="var(--color-accent-orange)" />
                    <path className="streak-part" d="M33.5589 145.048L33.1553 128.327L49.8805 144.557L33.5589 145.048Z" fill="var(--color-accent-orange)" />
                    <path className="streak-part" d="M67.1563 145.219L50.3918 128.327L49.8799 144.557L67.1563 145.219Z" fill="var(--color-accent-orange)" />
                    <path className="streak-part" d="M0 93.6715H18.3495L33.5587 111.185H15.8195L0 93.6715Z" fill="var(--color-accent-orange)" />
                    <path className="streak-part" d="M113.749 25.8239H130.582V43.4875L113.749 25.8239Z" fill="var(--color-accent-orange)" />
                    <path className="streak-part" d="M149.985 43.4874H166.169V60.0784L149.985 43.4874Z" fill="var(--color-accent-orange)" />
                    <path className="streak-part" d="M163.186 77.6318L180 60.0784H147.396L146.904 43.4874H130.582L163.186 77.6318Z" fill="var(--color-accent-orange)" />
                    <path className="streak-part" d="M73.4072 1.52377V19.1273H106.484V1.52377L95.655 12.3204L89.9454 0L83.7829 11.9696L73.4072 1.52377Z" fill="var(--color-accent-orange)" />
                </g>
            </svg>
            <span className="streak-badge-number streak-number-animate">{streak}</span>
        </div>
    );
}
